import clsx from 'clsx'
import dayjs from 'dayjs'
import { router } from 'expo-router'
import { memo, type ReactNode } from 'react'
import { View } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { Bullet } from '@/src/elements/bullet'
import { Card } from '@/src/elements/card'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { useRoutes } from '@/src/general/utils/routes'

import type { TGetConversationsData } from '../../../api/types/getConversationsApi.types'
import { useMarkConversationAsRead } from '../../../services/useMarkConversationAsReadMutation'
import { useConversationsListStore } from '../../../stores/useConversationsListStore'
import { ConversationOptions } from '../ConversationOptions'
import { PreviewContentText } from './PreviewContentText'

export type TConversationPreviewProps = {
  conversation: TGetConversationsData[number]
  isSelected: boolean
  openedConversationId: number | undefined
}

export const ConversationPreview = memo(
  ({ conversation, isSelected, openedConversationId }: TConversationPreviewProps): ReactNode => {
    const { routes } = useRoutes()
    const markAsRead = useMarkConversationAsRead()
    const isUnreadFilterActive = useConversationsListStore((state) => state.unread)
    const { latestMessage, conversationUsers, hasUnreadMessages, id } = conversation
    const user = conversationUsers[0].user

    const handlePress = () => {
      if (hasUnreadMessages && isUnreadFilterActive) {
        markAsRead(id)
      }
      router.navigate(routes.conversationById(id))
    }

    return (
      <Card
        testID={TEST_ID.CONVERSATION.PREVIEW.ITEM}
        onPress={handlePress}
        additionalProps={{ role: 'link' }}
        isPressable
        isHoverable
        shadow="none"
        className={clsx(
          'flex-row overflow-visible p-2 data-[hover=true]:translate-x-0 data-[hover=true]:translate-y-0',
          isSelected ? 'bg-primary-bg-medium' : 'bg-transparent data-[hover=true]:bg-default-bg-light',
        )}
      >
        <View className="flex-1 flex-row gap-3">
          <Avatar src={getAvatarUrl(user.avatar)} size="md" />
          <View className="flex-1 flex-col items-center justify-between">
            <View className="relative w-full flex-row items-center justify-between">
              <Typography numberOfLines={1} className="flex-1 text-ellipsis pr-8" bold>
                {user.name}
              </Typography>
              <ConversationOptions
                conversation={conversation}
                openedConversationId={openedConversationId}
                className="absolute -top-1 right-0"
                size="xs"
                testID={TEST_ID.CONVERSATION.PREVIEW.OPTIONS_BUTTON}
              />
            </View>
            <View className="flex w-full max-w-full flex-row gap-2">
              {latestMessage && (
                <Typography size="sm" numberOfLines={1} contrast="low" className="flex-1 text-ellipsis">
                  <PreviewContentText contactName={user.name} message={latestMessage} />
                </Typography>
              )}
              <View className="flex-row items-center gap-2 pr-2">
                {latestMessage && (
                  <Typography
                    size="sm"
                    bold={hasUnreadMessages}
                    colors={hasUnreadMessages ? 'primary' : 'default'}
                    contrast="low"
                  >
                    {dayjs().from(dayjs(latestMessage.updatedAt), true)}
                  </Typography>
                )}
                {hasUnreadMessages && <Bullet size="sm" showOutline={false} isDot />}
              </View>
            </View>
          </View>
        </View>
      </Card>
    )
  },
)
